import { Controller } from "@hotwired/stimulus"
import axios from "axios"

// Drag&drop kolejności pozycji i zapis pełnej tablicy przez /account/set_field.
// Używane w dwóch miejscach:
//   - dropdown konfiguracji kolumn tabeli (target=user_setting, mysz),
//   - konfiguracja dolnego paska apki mobilnej (target=user_setting, mysz + dotyk).
// Tylko zaznaczone (visible) wiersze są przeciągalne (mają data-...-target="item").
//
// Data-attributes na wrapperze (data-controller="columns-reorder"):
//   data-columns-reorder-save-url-value="<%= account_set_field_path %>"
//   data-columns-reorder-key-value="<dot.path>"
//   data-columns-reorder-save-target-value="account"   (opcjonalne; domyślnie user_setting)
// Na pozycjach: data-action="dragstart->... dragover->... drop->... dragend->..."
// + data-columns-reorder-target="item" + data-key. Uchwyt dotykowy:
// data-columns-reorder-handle (drag na dotyku startuje tylko z uchwytu).
export default class extends Controller {
  static targets = ["item"]
  static values = { saveUrl: String, key: String, saveTarget: String }

  // HTML5 drag&drop działa tylko z myszą — na dotyku dokładamy touch events
  // (delegowane na elemencie kontrolera), drag startuje z uchwytu.
  connect() {
    this.onTouchStart = this.touchStart.bind(this)
    this.onTouchMove = this.touchMove.bind(this)
    this.onTouchEnd = this.touchEnd.bind(this)
    this.element.addEventListener("touchstart", this.onTouchStart, { passive: true })
    this.element.addEventListener("touchmove", this.onTouchMove, { passive: false })
    this.element.addEventListener("touchend", this.onTouchEnd)
  }

  disconnect() {
    this.element.removeEventListener("touchstart", this.onTouchStart)
    this.element.removeEventListener("touchmove", this.onTouchMove)
    this.element.removeEventListener("touchend", this.onTouchEnd)
  }

  // --- mysz ---
  dragstart(event) {
    const item = event.target.closest("[data-columns-reorder-target='item']")
    if (!item) {
      event.preventDefault()
      return
    }
    this.draggedItem = item
    event.dataTransfer.effectAllowed = "move"
    item.classList.add("opacity-40")
  }

  dragover(event) {
    if (!this.draggedItem) return
    event.preventDefault()
    this.moveOver(event.target, event.clientY)
  }

  drop(event) {
    event.preventDefault()
  }

  dragend() {
    this.finishDrag()
  }

  // --- dotyk ---
  touchStart(event) {
    const handle = event.target.closest("[data-columns-reorder-handle]")
    if (!handle || !this.element.contains(handle)) return
    this.draggedItem = handle.closest("[data-columns-reorder-target='item']")
    if (this.draggedItem) this.draggedItem.classList.add("opacity-40")
  }

  touchMove(event) {
    if (!this.draggedItem) return
    event.preventDefault() // wstrzymaj scroll w trakcie przeciągania
    const touch = event.touches[0]
    this.moveOver(document.elementFromPoint(touch.clientX, touch.clientY), touch.clientY)
  }

  touchEnd() {
    this.finishDrag()
  }

  // Wstaw przeciągany element przed/za pozycją pod kursorem/palcem.
  moveOver(targetEl, clientY) {
    const over = targetEl && targetEl.closest("[data-columns-reorder-target='item']")
    if (!over || over === this.draggedItem || !this.element.contains(over)) return
    const rect = over.getBoundingClientRect()
    if (clientY < rect.top + rect.height / 2) {
      over.parentNode.insertBefore(this.draggedItem, over)
    } else {
      over.parentNode.insertBefore(this.draggedItem, over.nextSibling)
    }
  }

  finishDrag() {
    if (!this.draggedItem) return
    this.draggedItem.classList.remove("opacity-40")
    this.draggedItem = null
    this.saveOrder()
  }

  saveOrder() {
    const order = this.itemTargets.map((el) => el.dataset.key)
    const params = { target: this.saveTargetValue || "user_setting", key: this.keyValue, value: JSON.stringify(order) }

    axios.post(this.saveUrlValue, null, {
      params,
      headers: { Accept: "application/json" }
    }).catch((error) => console.error("columns reorder save failed", error))
  }
}
